/**
 * PageHeader — заголовок текущей страницы в топбаре.
 *
 * Аналог контракта support AppShell(title/subtitle/actions): бренд живёт в
 * сайдбаре (.rail), а заголовок раздела + действия — в топбаре (.topbar).
 *
 * Страницы задают заголовок через `usePageHeader({ title, subtitle, actions })`.
 * Пока страница не задала свой — берётся fallback по маршруту (ROUTE_TITLES),
 * поэтому топбар всегда осмысленный, даже до миграции конкретной страницы.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface PageHeader {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

interface PageHeaderState {
  header: PageHeader | null;
  setHeader: (header: PageHeader | null) => void;
}

const PageHeaderContext = createContext<PageHeaderState | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeader] = useState<PageHeader | null>(null);
  const value = useMemo<PageHeaderState>(() => ({ header, setHeader }), [header]);
  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

/**
 * Задать заголовок текущей страницы. Очищается при размонтировании.
 * `deps` — как у useEffect: меняйте при динамическом заголовке (имя участника).
 *
 * Допущение: одна активная страница задаёт заголовок за раз. При переходе A→B
 * React выполняет cleanup A (setHeader(null)) до setup B (setHeader(B)); кратковременный
 * null маскируется fallback'ом `routeHeader()` в useResolvedPageHeader, поэтому
 * мерцания заголовка нет. Если появятся две страницы, одновременно задающие
 * заголовок, — переходить на счётчик/last-writer-wins store.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePageHeader(header: PageHeader, deps: unknown[] = []): void {
  const ctx = useContext(PageHeaderContext);
  useEffect(() => {
    ctx?.setHeader(header);
    return () => ctx?.setHeader(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Fallback-заголовки по префиксу маршрута. */
const ROUTE_TITLES: { prefix: string; title: string; subtitle?: string }[] = [
  { prefix: '/overview', title: 'Обзор', subtitle: 'Сводка по продвижению KOMMA' },
  { prefix: '/pipe', title: 'Продажи', subtitle: 'Воронка продаж и выполнение финплана (Bitrix24)' },
  { prefix: '/campaigns', title: 'Кампании', subtitle: 'Боевой аккаунт — действия через апрув' },
  { prefix: '/anomalies', title: 'Аномалии', subtitle: 'Дневной мониторинг расхода и целей' },
  { prefix: '/antifraud', title: 'Антифрод / CRM', subtitle: 'Формы Директа ↔ сделки Bitrix' },
  { prefix: '/search-terms', title: 'Поисковые запросы', subtitle: 'Расход по запросам + кандидаты в минус-слова' },
  { prefix: '/semantics', title: 'Семантика', subtitle: 'Семантическое ядро и кластеры' },
  { prefix: '/seo/positions', title: 'SEO — Позиции', subtitle: 'Rank tracking (Яндекс + Google)' },
  { prefix: '/seo/keyword-map', title: 'SEO — Семантика и карта', subtitle: 'BOFU-first кластеры и гэп-лист' },
  { prefix: '/seo/competitors', title: 'SEO — Органик-конкуренты', subtitle: 'Органическая видимость (SoV)' },
  { prefix: '/seo/content', title: 'SEO — Контент', subtitle: 'Брифы, статусы страниц, гейт качества' },
  { prefix: '/seo/audits', title: 'SEO — Аудиты', subtitle: 'Коммерческие факторы Яндекса' },
  { prefix: '/seo/measure', title: 'SEO — Органика → MQL', subtitle: 'ROI органики (CPL ≤ 15 000 ₽)' },
  { prefix: '/seo/tasks', title: 'SEO — Задачи', subtitle: 'Бэклог находок и апрув-контур' },
  { prefix: '/seo', title: 'SEO — Обзор', subtitle: 'Органика → MQL и вклад в пайплайн' },
  { prefix: '/ai', title: 'AI-помощник', subtitle: 'Разговор с данными рекламы' },
  { prefix: '/memory', title: 'Память проекта', subtitle: 'Контекст и решения' },
  { prefix: '/users', title: 'Пользователи', subtitle: 'Команда KOMMA Ads' },
];

function routeHeader(pathname: string): PageHeader {
  // Сначала более длинные/специфичные префиксы (/admin/users перед /admin).
  const match = [...ROUTE_TITLES]
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find((r) => pathname === r.prefix || pathname.startsWith(r.prefix + '/'));
  return match ? { title: match.title, subtitle: match.subtitle } : { title: '' };
}

/** Итоговый заголовок для топбара: явный override страницы → иначе по маршруту. */
// eslint-disable-next-line react-refresh/only-export-components
export function useResolvedPageHeader(pathname: string): PageHeader {
  const ctx = useContext(PageHeaderContext);
  return ctx?.header ?? routeHeader(pathname);
}
